/**
 * Extract a vivid dominant color from an image URL.
 * Returns an HSL triple {h,s,l} and an rgb hex.
 *
 * We deliberately skip near-grey + very-dark + very-light pixels and
 * weight by saturation, so the picked color drives a visually meaningful
 * accent rather than averaging out to mud.
 */
export type Swatch = {
  hex: string;
  h: number;
  s: number;
  l: number;
};

function rgbToHsl(r: number, g: number, b: number) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h *= 60;
  }
  return { h, s, l };
}

function hslToHex(h: number, s: number, l: number) {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = h / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r = 0, g = 0, b = 0;
  if (hp >= 0 && hp < 1) [r, g, b] = [c, x, 0];
  else if (hp < 2) [r, g, b] = [x, c, 0];
  else if (hp < 3) [r, g, b] = [0, c, x];
  else if (hp < 4) [r, g, b] = [0, x, c];
  else if (hp < 5) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  const m = l - c / 2;
  const to = (v: number) => Math.round((v + m) * 255).toString(16).padStart(2, "0");
  return `#${to(r)}${to(g)}${to(b)}`;
}

export async function extractSwatch(imageUrl: string, signal?: AbortSignal): Promise<Swatch | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.referrerPolicy = "no-referrer";
    let settled = false;
    const done = (v: Swatch | null) => { if (!settled) { settled = true; resolve(v); } };
    if (signal) signal.addEventListener("abort", () => done(null));

    img.onerror = () => done(null);
    img.onload = () => {
      try {
        const SIZE = 48;
        const canvas = document.createElement("canvas");
        canvas.width = SIZE; canvas.height = SIZE;
        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) return done(null);
        ctx.drawImage(img, 0, 0, SIZE, SIZE);
        const { data } = ctx.getImageData(0, 0, SIZE, SIZE);

        // Histogram by hue bin, weighted by saturation*alpha.
        const bins = 24;
        const acc: { h: number; s: number; l: number; weight: number }[] =
          Array.from({ length: bins }, () => ({ h: 0, s: 0, l: 0, weight: 0 }));

        for (let i = 0; i < data.length; i += 4) {
          const a = data[i + 3];
          if (a < 200) continue;
          const r = data[i], g = data[i + 1], b = data[i + 2];
          const { h, s, l } = rgbToHsl(r, g, b);
          // skip extremes that wouldn't make a nice UI accent
          if (l < 0.18 || l > 0.88) continue;
          if (s < 0.18) continue;
          const idx = Math.floor((h / 360) * bins) % bins;
          const weight = s * s; // emphasise saturation
          acc[idx].h += h * weight;
          acc[idx].s += s * weight;
          acc[idx].l += l * weight;
          acc[idx].weight += weight;
        }

        let best = acc[0];
        for (const b of acc) if (b.weight > best.weight) best = b;
        if (best.weight === 0) {
          // fall back to overall average
          let r=0,g=0,bl=0,c=0;
          for (let i = 0; i < data.length; i += 4) {
            const a = data[i + 3];
            if (a < 200) continue;
            r += data[i]; g += data[i+1]; bl += data[i+2]; c++;
          }
          if (!c) return done(null);
          const av = rgbToHsl(r/c, g/c, bl/c);
          const s = Math.max(0.45, av.s);
          const l = Math.min(0.7, Math.max(0.45, av.l));
          return done({ h: av.h, s, l, hex: hslToHex(av.h, s, l) });
        }
        const h = best.h / best.weight;
        let s = best.s / best.weight;
        let l = best.l / best.weight;
        // boost so the accent feels alive even from a muted source
        s = Math.min(0.95, s * 1.15 + 0.05);
        l = Math.min(0.7, Math.max(0.45, l));
        done({ h, s, l, hex: hslToHex(h, s, l) });
      } catch {
        done(null);
      }
    };
    img.src = imageUrl;
  });
}

export function hslString({ h, s, l }: Swatch, lightness?: number) {
  const L = lightness ?? l;
  return `hsl(${h.toFixed(1)} ${(s * 100).toFixed(1)}% ${(L * 100).toFixed(1)}%)`;
}

export function hslaString({ h, s }: Swatch, l: number, a: number) {
  return `hsla(${h.toFixed(1)} ${(s * 100).toFixed(1)}% ${(l * 100).toFixed(1)}% / ${a})`;
}
